
import React, { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { useNavigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Form } from "@/components/ui/form";
import LoginFormFields from "./LoginFormFields";
import { useAuth } from "@/components/auth/AuthProvider";
import FormDivider from "../common/FormDivider";
import SocialLoginButtons from "../common/SocialLoginButtons";
import FormHeader from "../common/FormHeader";
import RegisterPrompt from "../common/RegisterPrompt";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  remember: z.boolean().optional()
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function LoginForm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  
  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      remember: false
    }
  });

  const handleSocialLogin = async (provider: "google" | "facebook") => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
        },
      });

      if (error) {
        toast.error(error.message);
      }
    } catch (error) {
      console.error(`${provider} login error:`, error);
      toast.error(`Failed to sign in with ${provider}`);
    }
  };

  const handleLogin = async (data: LoginFormData) => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });
      if (error) throw error;
      
      toast.success("Sign in successful");
      navigate("/dashboard");
    } catch (error: any) {
      toast.error(`Sign in failed: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <FormHeader 
        title="Welcome Back"
        subtitle="Sign in to access your account"
      />
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleLogin)} className="space-y-4">
          <LoginFormFields control={form.control} />
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {/* Remember me checkbox would go here if needed */}
            </div>
            <Button variant="link" className="text-sm font-medium text-grandpal-green p-0 h-auto" asChild>
              <Link to="/auth/forgot-password">Forgot password?</Link>
            </Button>
          </div>
          
          <Button 
            type="submit" 
            className="w-full h-11 bg-grandpal-green hover:bg-grandpal-green/90 text-white font-medium mt-4" 
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing in...
              </>
            ) : "Sign In"}
          </Button>
        </form>
      </Form>
      
      <FormDivider text="or sign in with" />
      
      <SocialLoginButtons 
        onGoogleClick={() => handleSocialLogin("google")}
        onFacebookClick={() => handleSocialLogin("facebook")}
      />
      
      <RegisterPrompt 
        text="Don't have an account?" 
        linkText="Create account"
        onClick={() => navigate("/auth/signup")} 
      />
    </div>
  );
}
